import {
  PaymentCardCardPayinJsonldPaymentPaymentCardPayinRead,
  PaymentCardJsonldPaymentPaymentCardPayinWrite,
} from '@/api/generated';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import useApi from 'hooks/useApi';
import { useRouter } from 'next/router';
import marketPlaceFactory from 'utils/factories/marketplace';

type MutationProps = {
  externalId: string;
  amount: number;
  redirectURL: string;
};

const usePostPayin = (
  onSuccess?: (data: PaymentCardCardPayinJsonldPaymentPaymentCardPayinRead, variables: MutationProps) => unknown,
) => {
  const queryClient = useQueryClient();
  const apiClient = useApi();
  const { push } = useRouter();

  const invalidateWalletQueries = () => {
    queryClient.invalidateQueries({ queryKey: marketPlaceFactory.wallet() });
    queryClient.invalidateQueries({ queryKey: marketPlaceFactory.walletHistory() });
  };

  const postPayin = useMutation({
    mutationKey: ['postDirectPayin'],
    mutationFn: async ({ externalId, amount, redirectURL }: MutationProps) => {
      const browserInfo = {
        acceptHeader: 'text/html, application/xhtml+xml, application/xml;q=0.9, /;q=0.8',
        javaEnabled: true,
        language: navigator.language,
        colorDepth: window ? window.screen.colorDepth : 0,
        screenHeight: window ? window.screen.height : 0,
        screenWidth: window ? window.screen.width : 0,
        timeZoneOffset: new Date().getTimezoneOffset(),
        userAgent: navigator.userAgent,
        javascriptEnabled: true,
      };
      const param: PaymentCardJsonldPaymentPaymentCardPayinWrite = {
        browserInfo,
        amount: Number(amount),
        redirectURL,
      };
      const res = await apiClient.payments.apiPaymentspaymentCardsExternalIdpayinPut(externalId, param);

      // Invalidate queries before redirecting to secure payment page
      invalidateWalletQueries();

      if (res.data.secureModeRedirectURL) {
        push(res.data.secureModeRedirectURL);
      } else {
        push(redirectURL);
      }

      return res.data;
    },
    onSuccess: (data, context) => {
      onSuccess?.(data, context);
    },
    onSettled: () => {
      // Invalidate queries wether the mutation is successful or not
      invalidateWalletQueries();
    },
  });

  return postPayin;
};

export default usePostPayin;
